import {
  makeStyles,
  AdornmentBox,
  Alert,
  FormControlLabel,
  Typography,
  Button,
  Checkbox,
  Collapse,
  FormSectionTitle,
  Grid,
  Tooltip
} from '@paintscout/ui'
import { KeypadInputField, DropdownSelect, FormSection, useClientOptions, useUser } from '@paintscout/ui-business'
import { EditableToggle, QuoteContext, WisetackDepositExplanation, useWisetack } from '@paintscout/react-quote'
import { getFeatures, getObjectLabels, getQuoteOptions, hasIntegrationInterface } from '@paintscout/util/builder'
import type { QuoteDocument, QuoteOptions } from 'paintscout'
import debounce from 'lodash/debounce'
import React, { useContext, useState } from 'react'
import clamp from 'lodash/clamp'
import BaseSidebarPage from '../BaseSidebarPage'

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: theme.spacing(),
    paddingBottom: theme.spacing(4)
  },
  input: {
    textAlign: 'right',
    width: '100%'
  },
  subtitle: {
    marginTop: -theme.spacing(2),
    color: theme.paintscout.palette.brand.Neutral.P75
  },
  badgeRoot: {
    flexShrink: 'unset',
    marginRight: 3
  }
}))

interface QuotePaymentsAndLendingProps {
  onToggleEdit?: (editStatus: boolean) => void
}

function QuotePaymentsAndLending({ onToggleEdit }: QuotePaymentsAndLendingProps) {
  const classes = useStyles()
  const { quote, isEditable, updateQuote } = useContext(QuoteContext)
  const { options } = useClientOptions()
  const { user, preferences, trialExpired, isSuperadmin } = useUser()
  const [invalidDeposit, setInvalidDeposit] = useState<'depositAmount' | 'wisetackDepositAmount'>(null)

  const { hasWisetack, getPayouts } = useWisetack({ options, preferences, user })

  const isTemplate = quote.type === 'quoteTemplate'
  const { templateSurchargeOverride } = getFeatures({ options })
  const hasStripe = hasIntegrationInterface({ preferences, options, integration: 'stripe' })
  const hasStripeW1d = hasIntegrationInterface({ preferences, options, integration: 'stripeW1d' })
  const hasPayrix = hasIntegrationInterface({ preferences, options, integration: 'payrix' })
  const hasPayments = hasStripe || hasStripeW1d || hasPayrix
  const isCanadianPayrix = options.options.integrations?.payrix?.config?.country === 'Canada' && hasPayrix
  const achLabel = isCanadianPayrix ? 'EFT' : 'ACH'
  const stagedPayoutsFeature = options.options.integrations?.wisetack?.config?.progressPaymentsEnabled

  const { quote: quoteLabel, currency } = getObjectLabels({ options, quote })
  const quoteOptions = getQuoteOptions({ options, quote })

  const [stateOptions, setStateOptions] = useState<QuoteOptions>(quoteOptions)

  const allowedDepositTypes = [
    { label: 'Credit Card', value: 'credit' },
    { label: achLabel, value: 'ach' },
    { label: `Credit Card or ${achLabel}`, value: 'both' }
  ]

  const [editToggleAnchor, setEditToggleAnchor] = useState<{
    top: number
    left: number
  }>(null)

  const debouncedUpdateQuote = debounce((quote, updateQuote: (args: { quote: QuoteDocument }) => void) => {
    updateQuote({ quote })
  }, 300)

  const handleCheckboxChange = (key: string, val: boolean) => {
    let newOptions = stateOptions
    if (key.includes('.')) {
      const [parent, child] = key.split('.')
      newOptions = {
        ...stateOptions,
        [parent]: {
          ...stateOptions[parent],
          [child]: val
        }
      }
    } else {
      newOptions = { ...stateOptions, [key]: val }
    }
    setStateOptions(newOptions)
    debouncedUpdateQuote({ ...quote, options: newOptions }, updateQuote)
  }

  const validateDepositAmount = (
    name: 'depositAmount' | 'wisetackDepositAmount',
    val: ('amount' | 'percentage') | number
  ) => {
    if (quote.totals.after_tax === 0) {
      return
    }
    if (typeof val === 'number') {
      const depositType = stateOptions[name]?.type
      setInvalidDeposit(
        (depositType === 'amount' && val >= quote.totals.after_tax) || (depositType === 'percentage' && val >= 100)
          ? name
          : null
      )
    } else {
      const depositAmount = stateOptions[name]?.value
      setInvalidDeposit(
        (val === 'amount' && depositAmount >= quote.totals.after_tax) || (val === 'percentage' && depositAmount >= 100)
          ? name
          : null
      )
    }
  }

  const updateDepositAmount = (
    name: 'depositAmount' | 'wisetackDepositAmount',
    key: string,
    val: ('amount' | 'percentage') | number
  ) => {
    validateDepositAmount(name, val)
    const depositType = stateOptions[name]?.type
    const newOptions = {
      ...stateOptions,
      [name]: {
        ...stateOptions[name],
        [key]:
          typeof val === 'string'
            ? val
            : depositType === 'amount'
              ? quote.totals.after_tax === 0
                ? val
                : clamp(val, 0, quote.totals.after_tax)
              : clamp(val, 0, 100)
      }
    }

    setStateOptions(newOptions)
    debouncedUpdateQuote({ ...quote, options: newOptions }, updateQuote)
  }

  const updateSurchargeAmount = (value: string) => {
    const newOptions = {
      ...stateOptions,
      surcharge: {
        ...stateOptions.surcharge,
        value: clamp(parseFloat(value), 0, 100)
      }
    }
    setStateOptions(newOptions)
    debouncedUpdateQuote({ ...quote, options: newOptions }, updateQuote)
  }

  const depositTypeOptions = [
    { label: '%', value: 'percentage' },
    { label: currency.symbol, value: 'amount' }
  ]

  const showWisetackDepositAmount =
    hasWisetack && !quote.is_invoice && stagedPayoutsFeature && !quote.totals.amount_paid && !quote.depositInfo

  return (
    <EditableToggle
      position={editToggleAnchor}
      editable={isEditable}
      disabled={trialExpired}
      onToggle={onToggleEdit}
      onClose={() => setEditToggleAnchor(null)}
    >
      <BaseSidebarPage className={classes.root}>
        <div
          onClick={(ev: React.MouseEvent) => {
            // only call ev.stopPropagation when we're not editable
            if (!isEditable) {
              ev.stopPropagation()
              setEditToggleAnchor({
                top: ev.clientY,
                left: ev.clientX
              })
            }
          }}
        >
          {hasPayments && (
            <>
              {quote.is_invoice ? (
                <>
                  <FormSection hideDivider>
                    <Typography variant="caption" className={classes.subtitle}>
                      Please note the settings currently listed below will be applied to any pending payment requests.
                    </Typography>
                  </FormSection>
                  <FormSectionTitle title={'Payment Methods'} variant={'h6'} />
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <FormControlLabel
                        label={'Credit Card'}
                        control={
                          <Checkbox
                            checked={stateOptions.allowPaymentsOnInvoices}
                            disabled={!isEditable}
                            onChange={(e, val) => handleCheckboxChange('allowPaymentsOnInvoices', val)}
                          />
                        }
                      />
                    </Grid>
                    {hasPayrix && (
                      <Grid item xs={12}>
                        <FormControlLabel
                          label={achLabel}
                          control={
                            <Checkbox
                              checked={stateOptions.allowACHPaymentsOnInvoices}
                              disabled={!isEditable}
                              onChange={(e, val) => handleCheckboxChange('allowACHPaymentsOnInvoices', val)}
                            />
                          }
                        />
                      </Grid>
                    )}
                  </Grid>
                </>
              ) : (
                <FormSectionTitle
                  title={'Allow Payments'}
                  variant={'h6'}
                  rightContent={
                    <Checkbox
                      style={{ marginRight: -12 }}
                      checked={
                        quote.is_invoice ? stateOptions.allowPaymentsOnInvoices : stateOptions.allowPaymentsOnQuotes
                      }
                      disabled={!isEditable}
                      onChange={(e, val) =>
                        handleCheckboxChange(
                          quote.is_invoice ? 'allowPaymentsOnInvoices' : 'allowPaymentsOnQuotes',
                          val
                        )
                      }
                    />
                  }
                />
              )}

              <Collapse show={!quote.is_invoice && stateOptions.allowPaymentsOnQuotes}>
                <FormSection>
                  <FormSectionTitle title={'Deposits'} variant={'h6'} />
                  <Grid container>
                    <Grid item xs={12}>
                      {hasPayrix && (
                        <DropdownSelect
                          label="Allow Deposit Payments By"
                          value={
                            allowedDepositTypes.find((option) => option.value === stateOptions.allowedDepositTypes) ||
                            allowedDepositTypes[0]
                          }
                          variant="single"
                          options={allowedDepositTypes}
                          onChange={(option) => {
                            handleCheckboxChange('allowedDepositTypes', option.value)
                          }}
                        />
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <KeypadInputField
                        value={stateOptions.depositAmount.value}
                        fullWidth={true}
                        margin="dense"
                        format={'price'}
                        disabled={!isEditable}
                        label={`Deposit Amount (${stateOptions.depositAmount.type === 'amount' ? currency.symbol : '%'})`}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                          updateDepositAmount('depositAmount', 'value', parseFloat(event.target.value))
                        }}
                        classes={{ inputInput: classes.input }}
                        autoSelect={true}
                        endAdornment={
                          <AdornmentBox>
                            <DropdownSelect
                              value={
                                depositTypeOptions.find((option) => option.value === stateOptions.depositAmount.type) ||
                                depositTypeOptions[0]
                              }
                              options={depositTypeOptions}
                              variant="single"
                              onChange={(option) => {
                                updateDepositAmount('depositAmount', 'type', option.value as 'percentage' | 'amount')
                              }}
                              disabled={!isEditable}
                              nestedDropdown
                            />
                          </AdornmentBox>
                        }
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Collapse show={invalidDeposit === 'depositAmount'}>
                        <Alert severity="error">
                          Deposit amount must be less than the total amount of the {quoteLabel.value.toLowerCase()}.
                        </Alert>
                      </Collapse>
                    </Grid>
                  </Grid>
                </FormSection>
              </Collapse>
              {!isCanadianPayrix && (
                <Collapse
                  show={
                    isTemplate
                      ? templateSurchargeOverride
                      : quote.is_invoice
                        ? stateOptions.allowPaymentsOnInvoices
                        : stateOptions.allowPaymentsOnQuotes
                  }
                >
                  <FormSection>
                    <FormSectionTitle
                      title={'Surcharging'}
                      variant={'h6'}
                      rightContent={
                        <Checkbox
                          style={{ marginRight: -12 }}
                          checked={stateOptions.surcharge?.enabled}
                          disabled={!isEditable}
                          onChange={(e, val) => handleCheckboxChange('surcharge.enabled', val)}
                        />
                      }
                    />
                    <Grid container alignItems="center">
                      <Grid item xs={12}>
                        <KeypadInputField
                          value={stateOptions.surcharge.value}
                          fullWidth={true}
                          margin="dense"
                          disabled={!isEditable || !stateOptions.surcharge?.enabled}
                          label={'Surcharge Amount (%)'}
                          format="quantity"
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            updateSurchargeAmount(event.target.value)
                          }}
                          classes={{ inputInput: classes.input }}
                          autoSelect={true}
                        />
                      </Grid>
                      {!quote.is_invoice && (
                        <Grid item xs={12}>
                          <FormControlLabel
                            label={'Apply surcharge to deposit'}
                            disabled={stateOptions.allowedDepositTypes === 'ach'}
                            control={
                              <Checkbox
                                checked={stateOptions.surcharge?.applyToDeposit}
                                disabled={!isEditable || !stateOptions.surcharge?.enabled}
                                onChange={(e, val) => handleCheckboxChange('surcharge.applyToDeposit', val)}
                              />
                            }
                          />
                        </Grid>
                      )}
                      {hasPayrix && (
                        <Grid item xs={12}>
                          <FormControlLabel
                            style={{ alignItems: 'flex-start', marginTop: 8 }}
                            label={
                              <Tooltip
                                classes={{ badgeRoot: classes.badgeRoot }}
                                content={`Debit card payments can't be surcharged due to legal restrictions. If this box is unchecked, customers will not be able to pay with debit while surcharging is enabled.`}
                              >
                                <>{"Allow debit card payments (surcharge won't apply to debit)."}</>
                              </Tooltip>
                            }
                            control={
                              <Checkbox
                                style={{ marginTop: -8 }}
                                checked={stateOptions.surcharge?.processDebit}
                                disabled={!isEditable || !stateOptions.surcharge?.enabled}
                                onChange={(e, val) => handleCheckboxChange('surcharge.processDebit', val)}
                              />
                            }
                          />
                        </Grid>
                      )}
                    </Grid>
                  </FormSection>
                </Collapse>
              )}
            </>
          )}
          {showWisetackDepositAmount && (
            <FormSection hideDivider={!hasPayments}>
              <FormSectionTitle title={'Financing'} variant={'h6'} />
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12}>
                  <KeypadInputField
                    toolTip={<WisetackDepositExplanation />}
                    value={stateOptions.wisetackDepositAmount.value}
                    fullWidth={true}
                    margin="dense"
                    format={'price'}
                    disabled={!isEditable}
                    label={`Wisetack Deposit Amount (${stateOptions.wisetackDepositAmount.type === 'amount' ? currency.symbol : '%'})`}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      updateDepositAmount('wisetackDepositAmount', 'value', parseFloat(event.target.value))
                    }}
                    classes={{ inputInput: classes.input }}
                    autoSelect={true}
                    style={{ width: '100%' }}
                    endAdornment={
                      <AdornmentBox>
                        <DropdownSelect
                          value={
                            depositTypeOptions.find(
                              (option) => option.value === stateOptions.wisetackDepositAmount.type
                            ) || depositTypeOptions[0]
                          }
                          options={depositTypeOptions}
                          variant="single"
                          onChange={(option) => {
                            updateDepositAmount(
                              'wisetackDepositAmount',
                              'type',
                              option.value as 'percentage' | 'amount'
                            )
                          }}
                          disabled={!isEditable}
                          nestedDropdown
                        />
                      </AdornmentBox>
                    }
                  />
                </Grid>
                {quote.totals.after_tax !== 0 && quote.totals.balance_due > 500 && (
                  <Grid item xs={12}>
                    <Alert severity={getPayouts(quote) ? 'success' : 'info'}>
                      {getPayouts(quote)
                        ? 'Financing will be split into two progress payments.'
                        : 'Financing will be paid in a single payout.'}
                    </Alert>
                  </Grid>
                )}
              </Grid>
            </FormSection>
          )}
          {quote.paymentError && isSuperadmin && (
            <FormSection>
              <FormSectionTitle title={'Payment Error'} variant={'h6'} />
              <Button
                error
                variant="dialog"
                color="primary"
                disabled={!quote.paymentError || !isEditable}
                onClick={() => {
                  updateQuote({ quote: { ...quote, paymentError: false } })
                }}
              >
                Clear Payment Error
              </Button>
            </FormSection>
          )}
        </div>
      </BaseSidebarPage>
    </EditableToggle>
  )
}

export default QuotePaymentsAndLending
